import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { isBookabilityAllowedWithBackgroundCheck } from "../../Bookability/isBookabilityAllowedWithBackgroundCheck";
import { ShiftBottomSheetCardsWrapper } from "../../BottomSheet/CardsWrapper";
import { ShiftFailedLoadingCard } from "../../BottomSheet/ShiftFailedLoadingCard";
import { resolveShiftType } from "../../resolveShiftType";
import { ShiftBundleBottomSheetFooter } from "./BundleFooter";
import { ShiftBundleBottomSheetContent } from "./Content";
import { ShiftBundleBottomSheetHeader } from "./Header";

export interface ShiftBundleBottomSheetProps {
  modalState: UseModalState;
}

export function ShiftBundleBottomSheet(props: ShiftBundleBottomSheetProps) {
  const { modalState } = props;

  const {
    shift,
    bookabilityStatus,
    blockingRequirementsContext,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    isIndependentContractorBadgesRequired,
    bundle,
    bundleShiftPays,
    navigateToModal,
  } = useShiftModalsDataContext();

  const { workplace } = shift ?? {};

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels([
    bundle?.qualification ?? "",
  ]);
  const [qualificationText] = qualificationDisplayLabels;

  const isBookable =
    isBookabilityAllowedWithBackgroundCheck(bookabilityStatus) &&
    isNil(shift?.attributes.windowRestriction) &&
    !blockingRequirementsContext?.hasBlockingRequirements;

  const sortedBundleShifts = useMemo(
    () =>
      [...(bundle?.shifts ?? [])].sort(
        (a, b) => parseISO(a.attributes.start).getTime() - parseISO(b.attributes.start).getTime()
      ),
    [bundle]
  );

  const hasRequiredData = isDefined(bundle) && isDefined(shift) && isDefined(workplace);

  const shiftType = hasRequiredData
    ? resolveShiftType({
        isUrgent: shift.attributes.isUrgent,
        window: shift.attributes.window,
        priorityTill: shift.attributes.priorityTill,
      })
    : undefined;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        hasRequiredData ? (
          <ShiftBundleBottomSheetFooter
            totalPayInMinorUnits={bundle.totalPayInMinorUnits}
            isInstantPay={shift.attributes.isInstantPay ?? false}
            isBookable={isBookable}
            onSelectShifts={() => {
              navigateToModal(SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH, {
                pathParams: { shiftId: shift.id },
              });
            }}
          />
        ) : undefined
      }
    >
      {hasRequiredData && (
        <>
          <ShiftBundleBottomSheetHeader
            bundle={bundle}
            sortedBundleShifts={sortedBundleShifts}
            qualificationText={qualificationText ?? bundle.qualification}
            workplaceTimezone={workplace.attributes.location.timezone}
            onClose={() => {
              modalState.closeModal();
            }}
          />

          {isDefined(bookabilityStatus) && isDefined(shiftType) && (
            <ShiftBundleBottomSheetContent
              shiftId={shift.id}
              shiftType={shiftType}
              shiftQualification={shift.attributes.qualification}
              shiftStartIso={shift.attributes.start}
              workplace={workplace}
              bookabilityStatus={bookabilityStatus}
              blockingRequirementsContext={blockingRequirementsContext}
              hasHoursRestrictionConflict={hasHoursRestrictionConflict ?? false}
              shiftFailedLoading={shiftFailedLoading}
              hasMarkedInterest={shift.attributes.isInterested}
              restrictedToWindow={shift.attributes.windowRestriction}
              isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
              window={shift.attributes.window}
              sortedBundleShifts={sortedBundleShifts}
              bundleShiftPays={bundleShiftPays}
            />
          )}
        </>
      )}

      {!hasRequiredData && shiftFailedLoading && (
        <ShiftBottomSheetCardsWrapper>
          <ShiftFailedLoadingCard />
        </ShiftBottomSheetCardsWrapper>
      )}
    </BottomSheet>
  );
}
